import { marketStatuses } from "../constants/market-statuses";

export function getStatus(status: number): string {
  return marketStatuses[status];
}

export function sortHighlightRunners(items: any[]) {
  // First, sort by sortPriority
  items?.sort((a, b) => a?.sortPriority - b?.sortPriority);

  // Find index of "The Draw"
  const drawIndex = items?.findIndex((item) =>
    item?.runnerName?.includes("The Draw"),
  );

  // If "The Draw" exists and it's not already the second element
  if (drawIndex > -1 && drawIndex !== 1) {
    const [drawItem] = items?.splice(drawIndex, 1); // Remove "The Draw" from its position
    items?.splice(1, 0, drawItem); // Insert "The Draw" at second position
  }

  return items;
}

export function genericSort<T>(
  array: T[],
  fields: (keyof T)[], // Accept an array of fields for sorting
  order: "asc" | "desc" = "asc",
): T[] {
  return array.sort((a, b) => {
    for (const field of fields) {
      const aValue = a[field];
      const bValue = b[field];

      let comparison = 0;

      // Handle Date objects or date-time strings
      if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else if (
        typeof aValue === "string" &&
        typeof bValue === "string" &&
        Date.parse(aValue) &&
        Date.parse(bValue)
      ) {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
      }

      // Fallback for numbers and strings
      else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      }

      // If comparison is non-zero, return the result, otherwise move to the next field
      if (comparison !== 0) {
        return order === "desc" ? -comparison : comparison;
      }
    }

    // If all fields are equal, return 0
    return 0;
  });
}

export function inningSort(array: any[]) {
  const regex = /(\d+)[a-z]{2}\sInnings\s(\d+)\sOvers/i;

  // Extract indices and values of "Innings" markets
  const inningsMarkets = array
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => regex.test(item.marketName));

  // Sort only the "Innings" markets
  const sortedInningsMarkets = inningsMarkets
    .map(({ item }) => item) // Extract the items
    .sort((a, b) => {
      const matchA = a.marketName.match(regex);
      const matchB = b.marketName.match(regex);

      // Safely extract inning and overs values
      const inningA = parseInt(matchA[1], 10); // First number (before "Innings")
      const oversA = parseInt(matchA[2], 10); // Second number (before "Overs")

      const inningB = parseInt(matchB[1], 10); // First number (before "Innings")
      const oversB = parseInt(matchB[2], 10); // Second number (before "Overs")

      // Sort by inning first, then by overs
      if (inningA !== inningB) {
        return inningA - inningB; // Sort by inning
      }
      return oversA - oversB; // Sort by overs if innings are the same
    });

  // Find the index of the first "Innings" market in the original array
  const firstInningIndex =
    inningsMarkets.length > 0 ? inningsMarkets[0].index : -1;

  // Build the result array
  const result = [...array];

  if (firstInningIndex !== -1) {
    // Remove all the original "Innings" markets
    const filteredResult = result.filter(
      ({ marketName }) => !regex.test(marketName),
    );

    // Insert the sorted "Innings" markets after the first detected "Innings" market
    return [
      ...filteredResult.slice(0, firstInningIndex),
      ...sortedInningsMarkets,
      ...filteredResult.slice(firstInningIndex),
    ];
  }

  // If no "Innings" markets are found, return the original array
  return result;
}
